import { FormattedMessage, useIntl } from "react-intl";
import ControlledSelect from "../../../../components/ControlledInputs/select";
import { StyledCheckbox } from "../styles";
import { Error, FormRow } from "../../../../components/ControlledInputs/styles";
import { useEffect, useRef } from "react";
import { MdError } from "react-icons/md";

/**
 * Componente para seleção de tipos disponíveis no fluxo
 */
const FlowTypeSelection = ({
  control,
  formState,
  watch,
  form,
  clientTypeOptions,
  serviceTypeOptions,
  actorTypeOptions,
  boothRequirementOptions,
  inactivate,
}) => {
  const intl = useIntl();
  const isUpdatingRef = useRef(false);
  const previousActorTypesRef = useRef([]);

  const handleClientCheckboxChange = (isChecked) => {
    form.setValue("allowClient", isChecked);
    if (!isChecked) {
      form.setValue("allowedClientTypes", []);
      form.clearErrors("allowedClientTypes");
    }
  };

  const handleServiceProviderCheckboxChange = (isChecked) => {
    form.setValue("allowServiceProvider", isChecked);
    if (!isChecked) {
      form.setValue("allowedServiceTypes", []);
      form.clearErrors("allowedServiceTypes");
    }
  };

  const allowedActorTypes = watch("allowedActorTypes");
  
  useEffect(() => {
    if (isUpdatingRef.current) {
      isUpdatingRef.current = false;
      previousActorTypesRef.current = allowedActorTypes || [];
      return;
    }

    const currentActorTypes = allowedActorTypes || [];
    const previousActorTypes = previousActorTypesRef.current || [];
    
    if (currentActorTypes.length === 0) {
      previousActorTypesRef.current = currentActorTypes;
      return;
    }

    const hasAll = currentActorTypes.some((opt) => opt?.value === "ALL");
    const hadAll = previousActorTypes.some((opt) => opt?.value === "ALL");
    const otherOptions = currentActorTypes.filter((opt) => opt?.value !== "ALL");
    const allOption = currentActorTypes.find((opt) => opt?.value === "ALL");

    if (hasAll && !hadAll && otherOptions.length > 0) {
      isUpdatingRef.current = true;
      form.setValue("allowedActorTypes", [allOption], {
        shouldValidate: true,
      });
    }
    else if (hasAll && hadAll && otherOptions.length > 0) {
      isUpdatingRef.current = true;
      form.setValue("allowedActorTypes", otherOptions, {
        shouldValidate: true,
      });
    }

    previousActorTypesRef.current = currentActorTypes;
  }, [allowedActorTypes, form]);

  return (
    <>
      {/* Primeira linha: Cliente e Prestador de Serviços lado a lado */}
      <FormRow style={{ gap: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Cliente */}
        <div style={{ flex: "1 1 300px", minWidth: "300px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <StyledCheckbox
              data-test="allow-client-checkbox"
              disabled={inactivate}
              id="allowClient"
              checked={watch("allowClient") || false}
              onChange={(e) => handleClientCheckboxChange(e.target.checked)}
            />
            <label
              htmlFor="allowClient"
              style={{ fontSize: "14px", fontWeight: "500", color: "#333" }}
            >
              <FormattedMessage id="client" />
              <span style={{ color: "#dc2626", marginLeft: "4px" }}>*</span>
            </label>
          </div>
          {formState.errors.allowClient && (
            <Error>
              <MdError color="#DC2626" size={14} />
              {intl.formatMessage({ id: "error.field_required" })}
            </Error>
          )}
          <div style={{ width: "100%" }}>
            <ControlledSelect
              data-test="allowed-client-types-select"
              name="allowedClientTypes"
              control={control}
              formState={formState}
              label="client_type"
              placeholder={intl.formatMessage({ id: "select" })}
              options={clientTypeOptions}
              required={watch("allowClient")}
              isMulti={true}
              closeMenuOnSelect={false}
              disabled={!watch("allowClient") || inactivate}
              errors={{
                allowedClientTypes: formState.errors.allowedClientTypes,
              }}
              inputProps={{ 'data-test': 'allowed-client-types-select' }}
            />
          </div>
        </div>

        {/* Prestador de Serviços */}
        <div style={{ flex: "1 1 300px", minWidth: "300px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <StyledCheckbox
              data-test="allow-service-provider-checkbox"
              disabled={inactivate}
              id="allowServiceProvider"
              checked={watch("allowServiceProvider") || false}
              onChange={(e) =>
                handleServiceProviderCheckboxChange(e.target.checked)
              }
            />
            <label
              htmlFor="allowServiceProvider"
              style={{ fontSize: "14px", fontWeight: "500", color: "#333" }}
            >
              <FormattedMessage id="SERVICE_PROVIDER" />
              {watch("allowServiceProvider") && (
                <span style={{ color: "#dc2626", marginLeft: "4px" }}>*</span>
              )}
            </label>
          </div>

          <div style={{ width: "100%" }}>
            <ControlledSelect
              data-test="allowed-service-types-select"
              name="allowedServiceTypes"
              control={control}
              formState={formState}
              label="service_type"
              placeholder={intl.formatMessage({ id: "select" })}
              options={serviceTypeOptions}
              required={watch("allowServiceProvider")}
              isMulti={true}
              closeMenuOnSelect={false}
              disabled={!watch("allowServiceProvider") || inactivate}
              errors={{
                allowedServiceTypes: formState.errors.allowedServiceTypes,
              }}
              inputProps={{ 'data-test': 'allowed-service-types-select' }}
            />
          </div>
        </div>
      </FormRow>

      {/* Segunda linha: Tipo de Usuário (Actor Type) e Cliente com Estande lado a lado */}
      <FormRow style={{ gap: "24px", marginTop: "24px", flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* Tipo de Usuário (Actor Type) */}
        <div style={{ flex: "1 1 300px", minWidth: "300px" }}>
          <ControlledSelect
            data-test="allowed-actor-types-select"
            name="allowedActorTypes"
            control={control}
            formState={formState}
            label="user_type"
            placeholder={intl.formatMessage({ id: "select" })}
            options={actorTypeOptions}
            required={true}
            isMulti={true}
            closeMenuOnSelect={false}
            disabled={inactivate}
            errors={{
              allowedActorTypes: formState.errors.allowedActorTypes,
            }}
            inputProps={{ 'data-test': 'allowed-actor-types-select' }}
          />
        </div>

        {/* Cliente com Estande */}
        <div style={{ flex: "1 1 300px", minWidth: "300px" }}>
          <ControlledSelect
            name="boothRequirement"
            control={control}
            formState={formState}
            label="upload_flow_booth_requirement"
            placeholder={intl.formatMessage({ id: "select" })}
            options={boothRequirementOptions}
            required={true}
            isMulti={false}
            disabled={inactivate}
            errors={{
              boothRequirement: formState.errors.boothRequirement,
            }}
            inputProps={{ 'data-test': 'booth-requirement-select' }}
          />
        </div>
      </FormRow>
    </>
  );
};

export default FlowTypeSelection;
